import { Box, Button, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { useActiveSession } from '@texas/hooks/useSession';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralManagement } from './GeneralManagement';
import { PeopleManagement } from './PeopleManagement';
import { TagsManagement } from './TagsManagement';
import { MigrationManagement } from './MigrationManagement';
import { TargetTiersManagement } from './TargetTiersManagement';
import { useClientConfiguration } from '@texas/hooks/useClientConfiguration';
import { CertificatesManagement } from './CertificatesManagement';

interface Props {
  articleId: number;
  onConfigure?: () => void;
  onCopy?: () => void;
}

type TabId =
  | 'general'
  | 'people'
  | 'tags'
  | 'migration'
  | 'target-tiers'
  | 'certificates';

export function ArticleManagement({ articleId, onConfigure, onCopy }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const { isProduction } = useClientConfiguration();
  const tabs: { id: TabId; label: string }[] = [
    { id: 'general', label: t('article.management.general') },
    { id: 'target-tiers', label: t('article.management.targetTiers') },
    { id: 'people', label: t('article.management.peopleAndRoles') },
    { id: 'tags', label: t('article.management.tags') },
    { id: 'certificates', label: t('complianceDocumentDefinitions.title') },
    ...(currentUser.betaFeatures && !isProduction
      ? [{ id: 'migration' as const, label: t('article.management.migration') }]
      : []),
  ];
  const {
    data: article,
    loading,
    error,
    refetch,
  } = useApiResource(articlesApi.getArticle);
  const refresh = useCallback(() => refetch(articleId), [articleId, refetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <BodyOverride>
      <Flex
        className="article-management"
        bg="articleManagement.surface"
        color="articleManagement.text"
        direction="column"
      >
        <Flex px={{ base: 6, xl: 10 }} pt={8} pb={7} justify="space-between">
          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              letterSpacing="0.12em"
              textTransform="uppercase"
              color="articleManagement.mutedText"
            >
              {t('article.management.eyebrow')}
            </Text>
            <Heading fontFamily="barlow" fontSize="4xl" lineHeight="1.1" mt={1}>
              {t('article.management.title')}
            </Heading>
            <Text color="articleManagement.mutedText" fontSize="sm" mt={1}>
              {t('article.management.description')}
            </Text>
          </Box>
          {activeTab === 'general' &&
            article &&
            article.matrixNodeXId > 0 &&
            article.matrixNodeYId > 0 && (
              <Flex align="flex-end" gap={3}>
                <Button variant="outline" onClick={onConfigure}>
                  {t('article.management.configureMatrix')}
                </Button>
              </Flex>
            )}
        </Flex>
        <Flex
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="articleManagement.border"
          px={{ base: 6, xl: 10 }}
          gap={8}
          h="58px"
        >
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant="unstyled"
              borderRadius={0}
              h="58px"
              px={0}
              fontSize="sm"
              fontWeight={activeTab === tab.id ? '600' : '400'}
              borderBottom="3px solid"
              borderColor={
                activeTab === tab.id
                  ? 'articleManagement.accent'
                  : 'transparent'
              }
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </Flex>
        <Box bg="articleManagement.canvas" flex={1} py={7}>
          <Box w="full">
            {loading && <Spinner />}
            {error && <ErrorDetails error={error} />}
            {article && activeTab === 'general' && (
              <GeneralManagement
                article={article}
                onUpdated={refresh}
                onCopy={onCopy}
              />
            )}
            {article && activeTab === 'people' && (
              <PeopleManagement article={article} />
            )}
            {activeTab === 'tags' && <TagsManagement articleId={articleId} />}
            {article && activeTab === 'certificates' && (
              <CertificatesManagement
                key={`${articleId}-${article.customerId}`}
                articleId={articleId}
                customerId={article.customerId}
              />
            )}
            {article &&
              currentUser.betaFeatures &&
              !isProduction &&
              activeTab === 'migration' && (
                <MigrationManagement article={article} onUpdated={refresh} />
              )}
            {activeTab === 'target-tiers' && (
              <TargetTiersManagement articleId={articleId} />
            )}
          </Box>
        </Box>
      </Flex>
    </BodyOverride>
  );
}
